import { useEffect, useRef, useCallback } from 'react';

const IDLE_TIMEOUT_MS = 10 * 60 * 1000;   // 10 minutes
const WARN_BEFORE_MS  =  1 * 60 * 1000;   // show warning 1 minute before logout

const ACTIVITY_EVENTS = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
    'wheel',
    'click',
];

/**
 * useIdleTimeout
 *
 * Automatically signs the user out after IDLE_TIMEOUT_MS of inactivity.
 * One minute before logout a warning callback is invoked so the UI can
 * display a countdown modal / banner.
 *
 * @param {Function} onIdle     – called when the idle timeout expires (trigger logout)
 * @param {Function} onWarn     – called when the warning threshold is reached
 * @param {Function} onActivity – called whenever activity resets the timer (optional)
 * @param {boolean}  enabled    – set to false to disable the hook entirely
 */
const useIdleTimeout = ({ onIdle, onWarn, onActivity, enabled = true }) => {
    const idleTimerRef = useRef(null);
    const warnTimerRef = useRef(null);
    const warnedRef   = useRef(false);

    const clearTimers = useCallback(() => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    }, []);

    const resetTimers = useCallback(() => {
        if (!enabled) return;

        clearTimers();
        warnedRef.current = false;

        // Warning timer fires 1 minute before logout
        warnTimerRef.current = setTimeout(() => {
            warnedRef.current = true;
            if (onWarn) onWarn();
        }, IDLE_TIMEOUT_MS - WARN_BEFORE_MS);

        // Logout timer
        idleTimerRef.current = setTimeout(() => {
            if (onIdle) onIdle();
        }, IDLE_TIMEOUT_MS);
    }, [enabled, clearTimers, onIdle, onWarn]);

    const handleActivity = useCallback(() => {
        if (!enabled) return;
        resetTimers();
        if (onActivity) onActivity();
    }, [enabled, resetTimers, onActivity]);

    useEffect(() => {
        if (!enabled) {
            clearTimers();
            return;
        }

        // Start timers immediately on mount / when enabled becomes true
        resetTimers();

        ACTIVITY_EVENTS.forEach(evt =>
            window.addEventListener(evt, handleActivity, { passive: true })
        );

        return () => {
            clearTimers();
            ACTIVITY_EVENTS.forEach(evt =>
                window.removeEventListener(evt, handleActivity)
            );
        };
    }, [enabled, resetTimers, handleActivity, clearTimers]);

    // Expose a manual reset so callers can reset after the user confirms "stay logged in"
    return { resetTimers };
};

export default useIdleTimeout;
